import { request } from '@mairistem/tools';
import _ from 'lodash';
import React from 'react';

import { ProduitContext } from '@/contexts';
import type { ProduitContextType } from '@/contexts/ProduitContext';
import { getProduits as get, getProduitsParametrage, getProduitsParametrageDetail } from '@/routes';
import type { ApiResponseType, ProduitType } from '@/types';
import { useRegieStore } from '@/zustand';

type ProduitContextProviderProps = {
  children: React.ReactNode;
  type?: 'regie' | 'parametrage' | 'parametrage-detail';
  // Produit à utiliser pour le filtrage en mode 'parametrage-detail'
  produit?: ProduitType;
};

const ProduitContextProvider = (props: ProduitContextProviderProps) => {
  const { children, type = 'regie', produit } = props;

  const [produits, setProduits] = React.useState<ProduitType[]>();
  const [loading, setLoading] = React.useState<boolean>(false);

  const autonome = useRegieStore((state) => state.regie?.autonome);
  const comptant = !!autonome;

  /** Lance la récupération des produits */
  const getProduits = React.useCallback(() => {
    const pendingPromises = [];

    setLoading(true);

    const route =
      type === 'parametrage'
        ? ({ page } = { page: 1 }) => getProduitsParametrage({ page, comptant })
        : type === 'parametrage-detail'
          ? ({ page } = { page: 1 }) => getProduitsParametrageDetail({ produit, page, comptant })
          : ({ page } = { page: 1 }) => get({ page, comptant });

    request.get(route()).then(({ data, meta: { lastPage } }: ApiResponseType<ProduitType>) => {
      for (let i = 2; i <= lastPage; i += 1) {
        pendingPromises.push(request.get(route({ page: i })));
      }

      Promise.all(pendingPromises)
        .then((datas) => {
          let dataFormatted: ProduitType[] = data;

          _.forEach(datas, ({ data: d }) => {
            dataFormatted = [...dataFormatted, ...d];
          });

          setProduits(dataFormatted);
        })
        .finally(() => {
          setLoading(false);
        });
    });
  }, [produit, type, comptant]);

  React.useEffect(() => {
    getProduits();
  }, [getProduits]);

  const context = React.useMemo<ProduitContextType>(
    () => ({
      produits,
      loading,
      refresh: getProduits,
    }),
    [produits, loading, getProduits],
  );

  return <ProduitContext.Provider value={context}>{children}</ProduitContext.Provider>;
};

export default ProduitContextProvider;
